import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MagnifyingGlass,
  UsersThree,
  X,
  DownloadSimple,
  Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

import api from "@/lib/api";
import CategoriaBadge from "../components/CategoriaBadge";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";

const emptyAsistencia = (eventoId, falleroId) => ({
  id: null,
  evento_id: eventoId,
  fallero_id: falleroId,
  asistira: null,
  acompanantes: 0,
  comentario: "",
  respuestas: {},
});

export default function EventoAsistencia() {
  const { id } = useParams();

  const [evento, setEvento] = useState(null);
  const [asistencias, setAsistencias] = useState([]);

  const [showGestionar, setShowGestionar] = useState(false);
  const [search, setSearch] = useState("");
  const [falleros, setFalleros] = useState([]);
  const [loadingFalleros, setLoadingFalleros] = useState(false);
  const [managedAsistencias, setManagedAsistencias] = useState({});
  const [openComments, setOpenComments] = useState({});

  // Tabla
  const [sortConfig, setSortConfig] = useState({
    key: "fallero",
    direction: "asc",
  });

  const [columnFilters, setColumnFilters] = useState({});

  const { can, canScope } = useAuth();

  const canManage = can(PERMISSIONS.MANAGE_ASISTENCIA);
  const canViewAsistencia = can(PERMISSIONS.VIEW_ASISTENCIA);

  const canManageEvento = (eventoData) =>
    canManage &&
    canScope(
      PERMISSIONS.MANAGE_ASISTENCIA,
      "asistencia",
      eventoData?.categoria,
    );

  const fetchData = async () => {
    try {
      const [ev, asis] = await Promise.all([
        api.get(`/eventos/${id}`),
        api.get(`/asistencias/evento/${id}`),
      ]);

      setEvento(ev.data);
      setAsistencias(asis.data);
    } catch (err) {
      toast.error("No se pudo cargar la asistencia");
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchFalleros = async (value = "") => {
    try {
      setLoadingFalleros(true);

      const params = new URLSearchParams();

      if (value.trim()) {
        params.set("search", value.trim());
      }

      const { data } = await api.get(`/falleros?${params.toString()}`);

      setFalleros(data);
    } catch (err) {
      toast.error("No se pudieron cargar los falleros");
    } finally {
      setLoadingFalleros(false);
    }
  };

  const openGestionar = () => {
    setShowGestionar(true);
    setSearch("");
    setFalleros([]);
    setManagedAsistencias({});
    setOpenComments({});
  };

  const closeGestionar = () => {
    setShowGestionar(false);
    setSearch("");
    setFalleros([]);
    setManagedAsistencias({});
    setOpenComments({});
  };

  const searchFalleros = (value) => {
    setSearch(value);
  };

  useEffect(() => {
    const value = search.trim();

    if (value.length < 3) {
      setFalleros([]);
      setLoadingFalleros(false);
      return;
    }

    const timeout = setTimeout(() => {
      fetchFalleros(value);
    }, 300);

    return () => clearTimeout(timeout);
  }, [search]);

  const getAsistencia = (falleroId) => {
    if (Object.prototype.hasOwnProperty.call(managedAsistencias, falleroId)) {
      return managedAsistencias[falleroId];
    }

    const existing = asistencias.find((a) => a.fallero_id === falleroId);

    return existing || emptyAsistencia(id, falleroId);
  };

  const updateManagedAsistencia = (falleroId, changes) => {
    setManagedAsistencias((prev) => ({
      ...prev,
      [falleroId]: {
        ...getAsistencia(falleroId),
        ...changes,
      },
    }));
  };

  const updateRespuesta = (falleroId, campoId, value) => {
    const asistencia = getAsistencia(falleroId);

    updateManagedAsistencia(falleroId, {
      respuestas: {
        ...(asistencia.respuestas || {}),
        [campoId]: value,
      },
    });
  };

  const saveManagedAsistencia = async (falleroId) => {
    try {
      const asistencia = getAsistencia(falleroId);

      const campoFaltante = getMissingRequiredQuestion(evento, asistencia);

      if (campoFaltante) {
        const fallero = falleros.find((f) => f.id === falleroId);

        toast.error(
          `${fallero?.nombre || "El fallero"} debe responder: "${campoFaltante.label}"`,
        );
        return;
      }

      const payload = {
        ...asistencia,
        evento_id: id,
        fallero_id: falleroId,
        respuestas: asistencia.respuestas || {},
        comentario: asistencia.comentario || "",
        acompanantes: Number(asistencia.acompanantes || 0),
      };

      const { data } = await api.post("/asistencias", payload);

      setManagedAsistencias((prev) => ({
        ...prev,
        [falleroId]: data,
      }));

      setAsistencias((prev) => {
        const exists = prev.some((item) => item.fallero_id === falleroId);

        if (!data.asistira) {
          return prev.filter((item) => item.fallero_id !== falleroId);
        }

        if (exists) {
          return prev.map((item) =>
            item.fallero_id === falleroId
              ? {
                  ...item,
                  ...data,
                }
              : item,
          );
        }

        const fallero = falleros.find((item) => item.id === falleroId);

        if (!fallero) {
          return prev;
        }

        return [
          ...prev,
          {
            ...data,
            fallero_nombre: `${fallero.nombre} ${fallero.apellidos}`.trim(),
            categoria: fallero.categoria,
          },
        ].sort((a, b) =>
          (a.fallero_nombre || "").localeCompare(b.fallero_nombre || "", "es"),
        );
      });

      toast.success("Asistencia guardada");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudo guardar la asistencia",
      );
    }
  };

  const getMissingRequiredQuestion = (evento, asistencia) => {
    if (asistencia?.asistira !== true) {
      return null;
    }

    const respuestas = asistencia?.respuestas || {};

    return (evento?.formulario || []).find((campo) => {
      if (!campo.required) return false;

      const value = respuestas[campo.id];

      return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && !value.trim())
      );
    });
  };

  /*
   * ============================================================
   * TABLA: COLUMNAS
   * ============================================================
   */

  const tableColumns = useMemo(() => {
    const columns = [
      {
        key: "fallero",
        label: "Fallero",
        getValue: (a) => a.fallero_nombre || "",
        filterType: "text",
      },
      {
        key: "categoria",
        label: "Categoría",
        getValue: (a) => a.categoria || "",
        filterType: "text",
      },
      {
        key: "asiste",
        label: "Asiste",
        getValue: (a) => (a.asistira ? "Sí" : "No"),
        filterType: "select",
        options: ["Sí", "No"],
      },
    ];

    (evento?.formulario || []).forEach((campo) => {
      columns.push({
        key: `campo_${campo.id}`,
        label: campo.label,
        getValue: (a) => {
          const value = a.respuestas?.[campo.id];

          if (campo.tipo === "checkbox") {
            return value ? "Sí" : "No";
          }

          if (value === null || value === undefined || value === "") {
            return "";
          }

          return String(value);
        },
        filterType:
          campo.tipo === "checkbox"
            ? "select"
            : campo.tipo === "select"
              ? "select"
              : "text",
        options:
          campo.tipo === "checkbox"
            ? ["Sí", "No"]
            : campo.tipo === "select"
              ? campo.options || []
              : [],
      });
    });

    columns.push({
      key: "comentario",
      label: "Comentario",
      getValue: (a) => a.comentario || "",
      filterType: "text",
    });

    return columns;
  }, [evento]);

  /*
   * ============================================================
   * TABLA: FILTROS
   * ============================================================
   */

  const updateColumnFilter = (key, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearColumnFilters = () => {
    setColumnFilters({});
  };

  const hasActiveFilters = Object.values(columnFilters).some(
    (value) => value !== undefined && value !== null && value !== "",
  );

  /*
   * ============================================================
   * TABLA: ORDENACIÓN
   * ============================================================
   */

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /*
   * ============================================================
   * TABLA: DATOS FILTRADOS Y ORDENADOS
   * ============================================================
   */

  const filteredAndSortedAsistencias = useMemo(() => {
    let result = [...asistencias];

    result = result.filter((asistencia) =>
      tableColumns.every((column) => {
        const filter = columnFilters[column.key];

        if (
          filter === undefined ||
          filter === null ||
          String(filter).trim() === ""
        ) {
          return true;
        }

        const value = String(column.getValue(asistencia) || "").toLowerCase();
        const filterValue = String(filter).toLowerCase();

        return value.includes(filterValue);
      }),
    );

    const sortColumn = tableColumns.find(
      (column) => column.key === sortConfig.key,
    );

    if (sortColumn) {
      result.sort((a, b) => {
        const valueA = String(sortColumn.getValue(a) ?? "");
        const valueB = String(sortColumn.getValue(b) ?? "");

        const comparison = valueA.localeCompare(valueB, "es", {
          numeric: true,
          sensitivity: "base",
        });

        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [asistencias, tableColumns, columnFilters, sortConfig]);

  /*
   * ============================================================
   * EXPORTAR EXCEL
   * ============================================================
   */

  const exportToExcel = () => {
    try {
      const rows = filteredAndSortedAsistencias.map((asistencia) => {
        const row = {};

        tableColumns.forEach((column) => {
          row[column.label] = column.getValue(asistencia);
        });

        return row;
      });

      if (rows.length === 0) {
        toast.error("No hay datos que exportar con los filtros actuales");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);

      const columnWidths = tableColumns.map((column) => {
        const maxLength = Math.max(
          column.label.length,
          ...rows.map((row) => String(row[column.label] ?? "").length),
        );

        return {
          wch: Math.min(Math.max(maxLength + 2, 12), 40),
        };
      });

      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Asistencia");

      const safeEventName = (evento?.titulo || "evento")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();

      XLSX.writeFile(workbook, `asistencia-${safeEventName || "evento"}.xlsx`);

      toast.success(`Se han exportado ${rows.length} asistentes`);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo generar el archivo Excel");
    }
  };

  /*
   * ============================================================
   * TABLA: CABECERA
   * ============================================================
   */

  const renderSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-med-muted/40 ml-1">↕</span>;
    }

    return (
      <span className="text-med-primary ml-1">
        {sortConfig.direction === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const renderColumnFilter = (column) => {
    const value = columnFilters[column.key] || "";

    if (column.filterType === "select") {
      return (
        <select
          value={value}
          onChange={(e) => updateColumnFilter(column.key, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="w-full mt-2 rounded-lg bg-med-surface border border-med-border px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-med-text outline-none focus:border-med-primary"
        >
          <option value="">Todos</option>

          {(column.options || []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    return (
      <div className="relative mt-2">
        <MagnifyingGlass
          size={13}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-med-muted"
        />

        <input
          type="text"
          value={value}
          onChange={(e) => updateColumnFilter(column.key, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder="Filtrar..."
          className="w-full rounded-lg bg-med-surface border border-med-border pl-7 pr-2 py-1.5 text-xs font-normal normal-case tracking-normal text-med-text placeholder:text-med-muted outline-none focus:border-med-primary"
        />
      </div>
    );
  };

  if (!evento) {
    return <div className="p-10 text-med-muted">Cargando...</div>;
  }

  const campos = evento.formulario || [];

  const asistenciaCerrada =
    evento.fecha_limite_asistencia &&
    new Date() > new Date(evento.fecha_limite_asistencia);

  const canManageThisEvent = canManageEvento(evento);

  // Quien tenga view_asistencia puede consultar/exportar la tabla completa.
  const canViewFullTable = canManageThisEvent || canViewAsistencia;

  return (
    <div className="p-6 md:p-10">
      <Link
        to="/calendario"
        className="inline-flex items-center gap-2 text-med-muted hover:text-med-text transition-colors mb-6"
      >
        <ArrowLeft size={18} />
        Volver a calendario
      </Link>

      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
          Eventos
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold text-med-text">
              Asistencia
            </h1>

            <p className="text-med-muted mt-2">{evento.titulo}</p>
          </div>

          <div className="flex items-center gap-2">
            {canManageThisEvent && !asistenciaCerrada && (
              <button
                type="button"
                onClick={openGestionar}
                className="inline-flex items-center justify-center gap-2 bg-med-primary hover:bg-med-primary-hover text-white rounded-xl px-5 py-2.5 font-semibold"
              >
                <UsersThree size={18} weight="bold" />
                Gestionar asistencia
              </button>
            )}
            {canViewFullTable && (
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center justify-center gap-2 bg-med-primary hover:bg-med-primary-hover text-white rounded-xl px-5 py-2.5 font-semibold"
              >
                <DownloadSimple size={18} weight="bold" />
                Descargar Excel
              </button>
            )}
          </div>
        </div>
      </header>

      {canViewFullTable ? (
        <>
          {/* =====================================================
              CONTROLES DE TABLA
              ===================================================== */}

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm text-med-muted">
              <Funnel size={17} />

              <span>
                {filteredAndSortedAsistencias.length} de {asistencias.length}{" "}
                registros
              </span>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearColumnFilters}
                  className="text-med-primary hover:text-med-primary-hover font-semibold"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>

          <div className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-med-surface2 text-xs uppercase tracking-[0.1em] text-med-muted font-semibold">
                    {tableColumns.map((column) => (
                      <th
                        key={column.key}
                        className="px-5 py-3 min-w-[150px] align-top"
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(column.key)}
                          className="flex items-center gap-1 hover:text-med-text transition-colors text-left"
                        >
                          <span>{column.label}</span>
                          {renderSortIndicator(column.key)}
                        </button>

                        {renderColumnFilter(column)}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredAndSortedAsistencias.length === 0 ? (
                    <tr>
                      <td
                        colSpan={tableColumns.length}
                        className="px-5 py-16 text-center text-med-muted"
                      >
                        {asistencias.length === 0
                          ? "Nadie ha respondido todavía."
                          : "No hay resultados para los filtros actuales."}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedAsistencias.map((a) => (
                      <tr key={a.id} className="border-t border-med-border/60">
                        <td className="px-5 py-4 font-semibold text-med-text">
                          {a.fallero_nombre}
                        </td>

                        <td className="px-5 py-4">
                          {a.categoria ? (
                            <CategoriaBadge categoria={a.categoria} />
                          ) : (
                            "—"
                          )}
                        </td>

                        <td className="px-5 py-4">
                          {a.asistira ? "Sí" : "No"}
                        </td>

                        {campos.map((c) => {
                          const value = a.respuestas?.[c.id];

                          return (
                            <td
                              key={c.id}
                              className="px-5 py-4 text-sm text-med-text"
                            >
                              {c.tipo === "checkbox"
                                ? value
                                  ? "Sí"
                                  : "No"
                                : String(value ?? "—")}
                            </td>
                          );
                        })}

                        <td className="px-5 py-4 text-sm text-med-muted">
                          {a.comentario || "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
            {asistencias.length === 0 ? (
              <div className="px-5 py-16 text-center text-med-muted">
                Nadie ha confirmado su asistencia todavía.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-med-surface2 text-xs uppercase tracking-[0.1em] text-med-muted font-semibold">
                      <th className="px-5 py-3">Fallero</th>
                    </tr>
                  </thead>

                  <tbody>
                    {asistencias.map((a) => (
                      <tr key={a.id} className="border-t border-med-border/60">
                        <td className="px-5 py-4 font-semibold text-med-text">
                          {a.fallero_nombre}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div className="mt-6 text-sm text-med-muted flex items-center gap-2">
        <UsersThree size={18} />
        {asistencias.filter((a) => a.asistira).length} asistentes confirmados
      </div>

      {/* =========================================================
          MODAL DE GESTIÓN DE ASISTENCIA
          ========================================================= */}

      {showGestionar && (
        <div
          className="fixed inset-0 z-50 bg-med-text/40 backdrop-blur-sm grid place-items-center p-4"
          onClick={closeGestionar}
        >
          <div
            className="bg-med-surface rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-display text-xl font-bold text-med-text">
                  Gestionar asistencia
                </h3>

                <p className="text-sm text-med-muted mt-1">{evento.titulo}</p>
              </div>

              <button
                type="button"
                onClick={closeGestionar}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 border-b border-med-border shrink-0">
              <div className="relative">
                <MagnifyingGlass
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-med-muted"
                />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => searchFalleros(e.target.value)}
                  placeholder="Buscar fallero por nombre, apellidos, DNI o email..."
                  autoFocus
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 pl-10 pr-4 py-3 outline-none"
                />
              </div>

              <p className="text-xs text-med-muted mt-2">
                Escribe al menos 3 caracteres para buscar.
              </p>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-3">
              {loadingFalleros ? (
                <div className="py-12 text-center text-med-muted">
                  Buscando falleros...
                </div>
              ) : search.trim().length < 3 ? (
                <div className="py-12 text-center text-med-muted">
                  Escribe al menos 3 caracteres.
                </div>
              ) : falleros.length === 0 ? (
                <div className="py-12 text-center text-med-muted">
                  No se han encontrado falleros.
                </div>
              ) : (
                falleros.map((fallero) => {
                  const asistencia = getAsistencia(fallero.id);

                  return (
                    <div
                      key={fallero.id}
                      className="border border-med-border rounded-2xl p-4 bg-med-surface2/30"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-bold text-med-text">
                            {fallero.nombre} {fallero.apellidos}
                          </div>

                          <div className="text-xs text-med-muted mt-1 flex items-center gap-2">
                            {fallero.categoria && (
                              <CategoriaBadge categoria={fallero.categoria} />
                            )}

                            {fallero.email && <span>{fallero.email}</span>}
                          </div>
                        </div>

                        <div className="flex gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() =>
                              updateManagedAsistencia(fallero.id, {
                                asistira: true,
                              })
                            }
                            className={`px-3 py-2 rounded-xl text-sm font-semibold ${
                              asistencia.asistira === true
                                ? "bg-emerald-600 text-white"
                                : "bg-med-surface2"
                            }`}
                          >
                            Asistirá
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              updateManagedAsistencia(fallero.id, {
                                asistira: false,
                              })
                            }
                            className={`px-3 py-2 rounded-xl text-sm font-semibold ${
                              asistencia.asistira === false
                                ? "bg-red-600 text-white"
                                : "bg-med-surface2"
                            }`}
                          >
                            No asistirá
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {asistencia.asistira === true &&
                          campos.length === 0 && (
                            <div className="flex items-end">
                              <span className="text-sm text-med-muted pb-2">
                                Sin preguntas adicionales.
                              </span>
                            </div>
                          )}
                      </div>

                      {asistencia.asistira === true && campos.length > 0 && (
                        <div className="mt-4 space-y-4">
                          {campos.map((campo) => (
                            <div key={campo.id}>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-med-muted mb-1">
                                {campo.label}
                                {campo.required ? " *" : ""}
                              </label>

                              {campo.tipo === "text" && (
                                <input
                                  type="text"
                                  value={
                                    asistencia.respuestas?.[campo.id] ?? ""
                                  }
                                  onChange={(e) =>
                                    updateRespuesta(
                                      fallero.id,
                                      campo.id,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                />
                              )}

                              {campo.tipo === "textarea" && (
                                <textarea
                                  rows={3}
                                  value={
                                    asistencia.respuestas?.[campo.id] ?? ""
                                  }
                                  onChange={(e) =>
                                    updateRespuesta(
                                      fallero.id,
                                      campo.id,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                />
                              )}

                              {campo.tipo === "number" && (
                                <input
                                  type="number"
                                  value={
                                    asistencia.respuestas?.[campo.id] ?? ""
                                  }
                                  onChange={(e) =>
                                    updateRespuesta(
                                      fallero.id,
                                      campo.id,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                />
                              )}

                              {campo.tipo === "checkbox" && (
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={
                                      asistencia.respuestas?.[campo.id] || false
                                    }
                                    onChange={(e) =>
                                      updateRespuesta(
                                        fallero.id,
                                        campo.id,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                  Sí
                                </label>
                              )}

                              {campo.tipo === "select" && (
                                <select
                                  value={
                                    asistencia.respuestas?.[campo.id] ?? ""
                                  }
                                  onChange={(e) =>
                                    updateRespuesta(
                                      fallero.id,
                                      campo.id,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full rounded-xl bg-med-surface2 px-3 py-2"
                                >
                                  <option value="">Selecciona...</option>

                                  {(campo.options || []).map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-4">
                        {openComments[fallero.id] || asistencia.comentario ? (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                setOpenComments((prev) => ({
                                  ...prev,
                                  [fallero.id]: false,
                                }))
                              }
                              className="text-sm font-semibold text-med-primary hover:text-med-primary-hover"
                            >
                              Ocultar comentario
                            </button>

                            <textarea
                              rows={2}
                              value={asistencia.comentario || ""}
                              onChange={(e) =>
                                updateManagedAsistencia(fallero.id, {
                                  comentario: e.target.value,
                                })
                              }
                              className="w-full rounded-xl bg-med-surface2 px-3 py-2 mt-2"
                              placeholder="Añade un comentario..."
                            />
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setOpenComments((prev) => ({
                                ...prev,
                                [fallero.id]: true,
                              }))
                            }
                            className="text-sm font-semibold text-med-primary hover:text-med-primary-hover"
                          >
                            + Añadir comentario
                          </button>
                        )}
                      </div>

                      <div className="flex justify-end mt-4 pt-4 border-t border-med-border">
                        <button
                          type="button"
                          onClick={() => saveManagedAsistencia(fallero.id)}
                          disabled={asistencia.asistira === null}
                          className="px-4 py-2 rounded-xl bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold"
                        >
                          Guardar asistencia
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
